import { isOhlc, movingAverage, type Ohlc } from "./chartScale.js";

/**
 * Technical indicators for a price chart, as plain arrays that line up index for index with the
 * prices they came from - ready to hand a `Chart` as a series' `values` (or a band's `values` and
 * `lower`). Each is null where it is not yet defined: before enough periods have passed, and
 * where a gap in the prices leaves it nothing honest to say.
 */

type Values = readonly (number | null | undefined)[];

const usable = (v: number | null | undefined): v is number => v !== null && v !== undefined && Number.isFinite(v);

/**
 * Exponential moving average: each value weighted `2 / (period + 1)` against the average so far,
 * seeded with the plain average of the first `period` values. A gap is passed over - its own entry
 * is null and the average carries on from the last real value.
 */
export function ema(values: Values, period: number): (number | null)[] {
  const n = Math.max(1, Math.floor(period));
  const k = 2 / (n + 1);
  const out: (number | null)[] = [];
  let seed = 0;
  let seen = 0;
  let avg: number | null = null;
  for (const v of values) {
    if (!usable(v)) {
      out.push(null);
      continue;
    }
    if (avg === null) {
      seed += v;
      seen++;
      if (seen === n) avg = seed / n;
      out.push(avg);
      continue;
    }
    avg = v * k + avg * (1 - k);
    out.push(avg);
  }
  return out;
}

export interface BollingerBands {
  middle: (number | null)[];
  upper: (number | null)[];
  lower: (number | null)[];
}

/**
 * Bollinger Bands: the `period` moving average, and `deviations` standard deviations of the same
 * window either side of it. Undefined over any window with a gap, as `movingAverage` is.
 */
export function bollinger(values: Values, period = 20, deviations = 2): BollingerBands {
  const n = Math.max(1, Math.floor(period));
  const middle = movingAverage(values as readonly (number | null)[], n);
  const upper: (number | null)[] = [];
  const lower: (number | null)[] = [];
  middle.forEach((m, i) => {
    if (m === null) {
      upper.push(null);
      lower.push(null);
      return;
    }
    let sum = 0;
    for (let j = i - n + 1; j <= i; j++) sum += ((values[j] as number) - m) ** 2;
    const sd = Math.sqrt(sum / n);
    upper.push(m + deviations * sd);
    lower.push(m - deviations * sd);
  });
  return { middle, upper, lower };
}

/**
 * Relative strength index, 0 to 100, with Wilder's smoothing: the average gain against the average
 * loss over `period` changes. Null for the first `period` values, and at a gap, after which it
 * starts over - a change measured across a gap is not a change from one period to the next.
 */
export function rsi(values: Values, period = 14): (number | null)[] {
  const n = Math.max(1, Math.floor(period));
  const out: (number | null)[] = [];
  let prev: number | null = null;
  let gains = 0;
  let losses = 0;
  let count = 0;
  let avgGain = 0;
  let avgLoss = 0;
  for (const v of values) {
    if (!usable(v)) {
      out.push(null);
      prev = null;
      gains = losses = count = 0;
      continue;
    }
    if (prev === null) {
      prev = v;
      out.push(null);
      continue;
    }
    const change = v - prev;
    prev = v;
    const gain = Math.max(0, change);
    const loss = Math.max(0, -change);
    count++;
    if (count < n) {
      gains += gain;
      losses += loss;
      out.push(null);
      continue;
    }
    if (count === n) {
      avgGain = (gains + gain) / n;
      avgLoss = (losses + loss) / n;
    } else {
      avgGain = (avgGain * (n - 1) + gain) / n;
      avgLoss = (avgLoss * (n - 1) + loss) / n;
    }
    out.push(avgLoss === 0 ? (avgGain === 0 ? 50 : 100) : 100 - 100 / (1 + avgGain / avgLoss));
  }
  return out;
}

export interface Macd {
  /** The fast EMA less the slow one. */
  macd: (number | null)[];
  /** The EMA of that line. */
  signal: (number | null)[];
  /** The line less its signal: the bars drawn under them. */
  histogram: (number | null)[];
}

/** Moving average convergence/divergence, with the usual 12, 26 and 9 by default. */
export function macd(values: Values, fast = 12, slow = 26, signalPeriod = 9): Macd {
  const f = ema(values, fast);
  const s = ema(values, slow);
  const line = f.map((a, i) => (a === null || s[i] === null ? null : a - s[i]!));
  const signal = ema(line, signalPeriod);
  const histogram = line.map((m, i) => (m === null || signal[i] === null ? null : m - signal[i]!));
  return { macd: line, signal, histogram };
}

/**
 * Volume-weighted average price: the running average of each period's typical price - the mean of
 * its high, low and close - weighted by its volume. A period with no prices or no volume adds
 * nothing and is null.
 */
export function vwap(prices: readonly (Ohlc | null)[], volume: Values): (number | null)[] {
  let weighted = 0;
  let total = 0;
  return prices.map((p, i) => {
    const vol = volume[i];
    if (!isOhlc(p) || !usable(vol) || vol <= 0) return null;
    weighted += ((p.high + p.low + p.close) / 3) * vol;
    total += vol;
    return weighted / total;
  });
}
